import { Injectable, Signal, signal } from '@angular/core'
import { AuthApiClient, AppUser } from '../services/ppv.api'
import { tap, shareReplay, switchMap, catchError } from 'rxjs/operators'
import { Router } from '@angular/router'
import { Observable, of } from 'rxjs'
import { RoleQuery } from '../models/Routing'
import { map } from 'lodash-es'
import { toSignal } from '@angular/core/rxjs-interop'
import { A } from '@angular/cdk/activedescendant-key-manager.d-Bjic5obv'

@Injectable()
export class AuthManager {
   private cachedUser$: Observable<AppUser>
   public ssoLogin$: Observable<boolean>
   public user = signal<AppUser>(undefined)

   constructor(private authApiClient: AuthApiClient, private router: Router) {
      const urlParams = new URLSearchParams(window.location.search)
      const sso = urlParams.get('sso')
      const sharepointToken = urlParams.get('sharepointToken')
      const clientId = urlParams.get('clientId')

      if (sso) {
         this.ssoLogin$ = this.authApiClient.sSO(sso).pipe(
            switchMap((res: any) => {
               if (!!res?.token) {
                  this.setToken(res.token)
                  return of(true)
               }

               return of(false)
            }),
            catchError((err) => {
               return of(false)
            }),
            shareReplay(1)
         )
      } else if (sharepointToken) {
         this.ssoLogin$ = this.authApiClient.sharepointSSO(sharepointToken, clientId).pipe(
            switchMap((res: any) => {
               if (!!res?.token) {
                  this.setToken(res.token)
                  return of(true)
               }

               return of(false)
            }),
            catchError((err) => {
               return of(false)
            }),
            shareReplay(1)
         )
      } else {
         this.ssoLogin$ = of(false).pipe(shareReplay(1))
      }
   }

   public isAuthenticated() {
      return !this.isExpired()
   }

   public clearAndRedirect() {
      this.clearToken()
      this.redirectToLogIn()
   }

   public redirectToLogIn() {
      // Preserve a handoff target across the bounce to login (e.g. the app-load whoAmI 401),
      // otherwise navigating to a bare ['login'] drops the ?redirect= param.
      const redirect = new URLSearchParams(window.location.search).get('redirect')
      this.router.navigate(['login'], redirect ? { queryParams: { redirect } } : undefined)
   }

   public tryLogIn({ username, email, password }) {
      return this.authApiClient.logIn({ email, password, loginName: username }).pipe(
         tap((res) => {
            if (res.success) {
               this.setToken(res.token)
            }
         }),
         shareReplay()
      )
   }

   public canAccess(user: AppUser, query: RoleQuery) {
      if (query == null) {
        return true
      }

      //managers can get access without holding the application role, so we don't have to
      //explicitly grant the role to every manager and keep that list up to date
      if (query.managerAccess && this.isManager(user)) {
         return true
      }

      const match = user.roles.find((x) => {
         //check 'permissions' roles which will have x.application=0 in the user roles
         //therefore, just match the role name which must be passed in
         if (!x.application && query.roleName) {
            return (
               (typeof query.roleName == 'string' && x.roleName === query.roleName) ||
               (Array.isArray(query.roleName) && query.roleName.indexOf(x.roleName) > -1)
            )
         }
         //check app roles which have the proper application id in the user roles
         else {
            return (
               x.application === query.application &&
               (!query.roleName ||
                  (typeof query.roleName == 'string' && x.roleName === query.roleName) ||
                  (Array.isArray(query.roleName) && query.roleName.indexOf(x.roleName) > -1))
            )
         }
      })
      return !!match
   }

   private static readonly MANAGER_JOB_CODES = ['HM', 'OPS-RM', 'COSA', 'VET-COS', 'OPS-SVP', 'OPS-VP', 'CORP-VP', 'CORP-DIR']
   private static readonly MANAGER_ROLES = ['HospitalManager', 'RM', 'OpsVP', 'OpsDirector']

   public isManager(user: AppUser) {
      return AuthManager.MANAGER_JOB_CODES.includes(user?.dayforceJobCode) || this.canAccess(user, { roleName: AuthManager.MANAGER_ROLES })
   }

   public getCurrentUser() {
      if (!this.cachedUser$) {
         this.cachedUser$ = this.ssoLogin$.pipe(
            switchMap(() => this.authApiClient.whoAmI()),
            tap((user) => this.user.set(user)),
            shareReplay(1)
         )
      }

      return this.cachedUser$
   }

   public clearToken() {
      this.cachedUser$ = null
      localStorage.removeItem('jwt_token')
   }

   public getToken() {
      return localStorage.getItem('jwt_token')
   }

   public setToken(token: string) {
      localStorage.setItem('jwt_token', token)
   }

   private isExpired() {
      const token = this.getToken()
      if (token == null) {
         return true
      }

      try {
         const decoded = this.decodeToken(token)
         const expiration = parseInt(decoded.exp, 10)
         if (expiration == null || isNaN(expiration)) {
            return true
         }

         const currentTime = new Date().getTime() / 1000
         return currentTime > expiration
      } catch (err) {
         console.error(err)
         return false
      }
   }

   private decodeToken(token: string) {
      if (typeof token !== 'string') {
         throw new Error('Invalid token specified')
      }

      try {
         // we don't care about the header
         return JSON.parse(atob(token.split('.')[1]))
      } catch (e) {
         throw new Error('Invalid token specified: ' + e.message)
      }
   }
}
